export type RawColorPalette = {
  primary: string | null
  secondary: string | null
  accent: string | null
  palette: Array<{ hex: string; population: number; rgb: [number, number, number] }>
  background: {
    type: 'white' | 'black' | 'transparent' | 'solid'
    color: string | null
    coverage: number
  } | null
}

export type AdaptedPalette = {
  primary: string
  secondary: string
  accent: string
  gradientCSS: string
  glassBackground: string
  glassBorder: string
}

type HSL = { h: number; s: number; l: number }

const hexToHsl = (hex: string): HSL => {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }

  const l = (max + min) / 2
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  return { h, s, l }
}

const hslToHex = ({ h, s, l }: HSL): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else { r = c; b = x }

  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const hslToRgba = ({ h, s, l }: HSL, alpha: number): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else { r = c; b = x }

  return `rgba(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}, ${alpha})`
}

const interpolateHue = (h1: number, h2: number, t: number): number => {
  let diff = h2 - h1
  if (diff > 180) diff -= 360
  if (diff < -180) diff += 360
  return ((h1 + diff * t) % 360 + 360) % 360
}

// Adapt colors per mode. Each role (primary/secondary/accent) gets a slightly
// different lightness offset so the gradient has visible variation for animation.
// Saturation is muted so the gradient is a subtle wash, not competing with content.
const adaptColor = (hsl: HSL, mode: 'light' | 'dark', role: 'primary' | 'secondary' | 'accent' = 'primary'): HSL => {
  const lightnessOffset = { primary: 0, secondary: 0.04, accent: -0.03 }[role]
  const saturationBoost = { primary: 0, secondary: 0.05, accent: 0.08 }[role]

  if (mode === 'light') {
    return {
      h: hsl.h,
      s: Math.min(Math.max(hsl.s + saturationBoost, 0.15), 0.35),
      l: Math.min(Math.max(0.90 + lightnessOffset, 0.84), 0.94),
    }
  }
  return {
    h: hsl.h,
    s: Math.min(Math.max(hsl.s + saturationBoost, 0.25), 0.50),
    l: Math.min(Math.max(0.12 + lightnessOffset, 0.08), 0.18),
  }
}

const buildGradientCSS = (
  primary: HSL,
  secondary: HSL,
  accent: HSL,
): string => {
  const mid: HSL = {
    h: interpolateHue(primary.h, secondary.h, 0.5),
    s: (primary.s + secondary.s) / 2,
    l: (primary.l + secondary.l) / 2,
  }

  const glow1 = `radial-gradient(ellipse at 75% 15%, ${hslToRgba(accent, 0.7)} 0%, ${hslToRgba(accent, 0)} 55%)`
  const glow2 = `radial-gradient(ellipse at 15% 85%, ${hslToRgba(secondary, 0.5)} 0%, ${hslToRgba(secondary, 0)} 55%)`
  const base = `linear-gradient(140deg, ${hslToHex(accent)} 0%, ${hslToHex(primary)} 35%, ${hslToHex(mid)} 65%, ${hslToHex(secondary)} 100%)`

  return `${glow1}, ${glow2}, ${base}`
}

export const parseColorPalette = (raw: unknown): RawColorPalette | null => {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  if (typeof obj.primary !== 'string') return null
  return raw as RawColorPalette
}

export const adaptPalette = (
  raw: RawColorPalette,
  mode: 'light' | 'dark',
): AdaptedPalette | null => {
  if (!raw.primary) return null

  const primaryHsl = adaptColor(hexToHsl(raw.primary), mode, 'primary')
  const secondaryHsl = raw.secondary
    ? adaptColor(hexToHsl(raw.secondary), mode, 'secondary')
    : adaptColor({ h: (primaryHsl.h + 45) % 360, s: primaryHsl.s, l: primaryHsl.l }, mode, 'secondary')
  const accentHsl = raw.accent
    ? adaptColor(hexToHsl(raw.accent), mode, 'accent')
    : adaptColor({ h: (primaryHsl.h + 320) % 360, s: primaryHsl.s, l: primaryHsl.l }, mode, 'accent')

  const gradientCSS = buildGradientCSS(primaryHsl, secondaryHsl, accentHsl)

  return {
    primary: hslToHex(primaryHsl),
    secondary: hslToHex(secondaryHsl),
    accent: hslToHex(accentHsl),
    gradientCSS,
    glassBackground: mode === 'light'
      ? 'rgba(255, 255, 255, 0.45)'
      : 'rgba(0, 0, 0, 0.35)',
    glassBorder: mode === 'light'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(255, 255, 255, 0.08)',
  }
}
